import { FC, memo, useCallback, useMemo, useRef, useState } from 'react';
import Typography from '@mui/material/Typography';
import { styled } from '@mui/material/styles';
import StyledTextField from '../../../../../components/atoms/TextField';
import InputAdornment from '@mui/material/InputAdornment';
import { faSearch } from '@fortawesome/free-solid-svg-icons/faSearch';
import { faTimes } from '@fortawesome/free-solid-svg-icons/faTimes';
import CheckboxNative from 'components/molecules/CheckboxNative';
import { grey } from 'theme/colors';
import { Option } from 'shared/types/api/getAuctions';
import { StyledFontAwesomeIconMui5 } from '../../../../../components/atoms/FontAwesomeIcon';

const OptionsBox = styled('div')(
  ({ theme }) => `
  margin: ${theme.spacing(0, 0, 1.5, 0)};
  max-height: ${theme.spacing(30)};
  overflow-y: scroll;
  padding: ${theme.spacing(1.5)};
  font-size: 11px;`,
);

// noinspection CssUnusedSymbol
const ClearAdornment = styled(InputAdornment)`
  &.MuiInputAdornment-root {
    cursor: pointer;
    height: 100%;
  }
`;

const OptionContainer = styled('div')(
  ({ theme }) => `
  display: flex;
  padding: ${theme.spacing(0.5, 0)};
  align-items: center;
  width: 100%;`,
);

const OptionLabelContainer = styled('div')`
  padding-left: 1em;
  font-size: 12px;
  color: #223559;
  cursor: pointer;
  width: 100%;
  display: flex;
  align-items: center;
`;

const ColorMarker = styled('span', { skipSx: false })`
  width: 1.5em;
  height: 1.5em;
  margin-right: 1em;
  border-radius: 0.2em;
  box-shadow: 0 1px 2px ${grey[400]};
`;

const OptionTextDisabled = styled('span', {
  shouldForwardProp: (prop) => prop !== 'isDisabled',
})<{
  isDisabled?: boolean;
}>(({ isDisabled }) => {
  if (isDisabled) {
    return `color: grey`;
  }
  return '';
});

function adsColorToCss(color: string) {
  if (color.toLowerCase() === 'copper') {
    //regarding ADS docs only copper invalid for css
    return '#b87333';
  } else {
    return color.toLowerCase();
  }
}

function OptionCheckboxLabeled({
  name,
  // count,
  checked,
  disabled,
  label,
  onChange,
  filterKey,
  withColor = false,
}): JSX.Element {
  if (!label) {
    return null;
  }
  return (
    <OptionContainer>
      <CheckboxNative
        name={name}
        checked={checked}
        onChange={(e) => {
          onChange(filterKey, e.target.name, e.target.checked);
        }}
        disabled={disabled}
        size={'2em'}
      />
      <OptionLabelContainer
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          onChange(filterKey, name, !checked);
        }}
      >
        {withColor ? (
          <ColorMarker
            sx={{ backgroundColor: adsColorToCss(label) }}
          ></ColorMarker>
        ) : null}
        <OptionTextDisabled isDisabled={disabled}>{label}</OptionTextDisabled>
        {/* &nbsp;&nbsp;
        <span
          className={`${classes.optionCounter} ${
            disabled ? classes.optionTextDisabled : ''
          }`}
        >
          ({count})
        </span> */}
      </OptionLabelContainer>
    </OptionContainer>
  );
}

function areEqual(prevProps, nextProps) {
  return (
    prevProps.checked === nextProps.checked &&
    prevProps.label === nextProps.label &&
    prevProps.name === nextProps.name &&
    prevProps.count === nextProps.count &&
    prevProps.filterKey === nextProps.filterKey &&
    prevProps.onChange === nextProps.onChange &&
    prevProps.disabled === nextProps.disabled
  );
}

const MemoizedOptionCheckbox: FC<any> = memo(OptionCheckboxLabeled, areEqual);

function OptionFilterSection({
  options: initialOptions,
  disabled,
  onChange,
  optionLabelFormatter,
  selected = new Set([]),
  filterKey,
  withColor = false,
  withAdornment = true,
}: {
  filterKey: string;
  options: Option[];
  disabled: boolean;
  selected?: Set<string>;
  onChange: (filterKey: string, name: string, checked: boolean) => void;
  optionLabelFormatter?: (label: string) => string;
  withColor?: boolean;
  withAdornment?: boolean;
}): JSX.Element {
  const [searchText, setSearchText] = useState('');
  const isChecked = useCallback((name) => selected.has(name), [selected]);

  const handleSearch = (event) => {
    setSearchText(event?.target?.value);
  };

  const searchRef = useRef<HTMLDivElement>();

  const isOptionDisabled = useCallback(
    (name: string, count: number) =>
      disabled || (count === 0 && !isChecked(name)),
    [disabled, isChecked],
  );

  const optionsToRender = useMemo(
    () =>
      (initialOptions || [])
        .filter(
          (o) =>
            !searchText ||
            o.name.toLocaleLowerCase().includes(searchText.toLocaleLowerCase()),
        )
        .map(({ name, count }) => ({
          name,
          count,
          checked: isChecked(name),
          onChange,
          disabled: isOptionDisabled(name, count),
          label: optionLabelFormatter ? optionLabelFormatter(name) : name,
        })),
    [
      initialOptions,
      searchText,
      isChecked,
      isOptionDisabled,
      onChange,
      optionLabelFormatter,
    ],
  );

  if (!initialOptions?.length) {
    return <Typography>Not available</Typography>;
  }

  return (
    <>
      {withAdornment && (
        <StyledTextField
          fullWidth
          size='small'
          onChange={handleSearch}
          sx={{
            '& .MuiOutlinedInput-input': {
              paddingLeft: 0,
            },
            marginTop: 0,
          }}
          InputProps={{
            ref: searchRef,
            startAdornment: (
              <InputAdornment position='start'>
                <StyledFontAwesomeIconMui5 icon={faSearch} size='sm' />
              </InputAdornment>
            ),
            endAdornment: searchText && (
              <ClearAdornment
                position='start'
                onClick={() => {
                  const input = searchRef.current?.querySelector('input');
                  if (input) {
                    input.value = '';
                  }
                  handleSearch({ target: { value: '' } });
                }}
              >
                <StyledFontAwesomeIconMui5 icon={faTimes} size='sm' />
              </ClearAdornment>
            ),
          }}
        />
      )}
      <OptionsBox>
        {optionsToRender.length > 0 ? (
          optionsToRender.map((option) => (
            <MemoizedOptionCheckbox
              key={option.name}
              filterKey={filterKey}
              withColor={withColor}
              {...option}
            />
          ))
        ) : (
          <Typography>Options not found</Typography>
        )}
      </OptionsBox>
    </>
  );
}

export default OptionFilterSection;
